/** Editing Tools → Blur prep settings + per-tool SEO variants. */

export const BLUR_TYPES = {
  BOKEH: "bokeh",
  GAUSSIAN: "gaussian",
  MOTION: "motion",
  PIXELATE: "pixelate",
  SQUARE_BOKEH: "square_bokeh",
  BOX: "box",
  DISC: "disc",
};

/** Chip labels — full names; CSS ellipsis truncates in the 3-col grid. */
export const BLUR_TYPE_OPTIONS = [
  { id: BLUR_TYPES.BOKEH, label: "Bokeh" },
  { id: BLUR_TYPES.GAUSSIAN, label: "Gaussian" },
  { id: BLUR_TYPES.MOTION, label: "Motion" },
  { id: BLUR_TYPES.PIXELATE, label: "Pixelate" },
  { id: BLUR_TYPES.SQUARE_BOKEH, label: "Square Pixel" },
  { id: BLUR_TYPES.BOX, label: "Box" },
  { id: BLUR_TYPES.DISC, label: "Disc" },
];

/** Pixel Blur shape chips (Square lives here — not as a separate blur type). */
export const PIXEL_SHAPES = {
  HEX: "hex",
  SQUARE: "square",
  CIRCLE: "circle",
  DIAMOND: "diamond",
};

export const PIXEL_SHAPE_OPTIONS = [
  { id: PIXEL_SHAPES.HEX, label: "Hex" },
  { id: PIXEL_SHAPES.SQUARE, label: "Square" },
  { id: PIXEL_SHAPES.CIRCLE, label: "Circle" },
  { id: PIXEL_SHAPES.DIAMOND, label: "Diamond" },
];

export const BLUR_AMOUNT_MIN = 0;
export const BLUR_AMOUNT_MAX = 100;
/** Visible blur by default so preview looks intentional (Photoroom starts at 0). */
export const BLUR_AMOUNT_DEFAULT = 20;

/** Default cell size for Pixel Blur. */
export const PIXEL_CELL_SIZE_DEFAULT = 10;
export const PIXEL_SOFTNESS_DEFAULT = 0;
export const PIXEL_GAP_DEFAULT = 0;
export const PIXEL_MIX_DEFAULT = 100;
export const PIXEL_SHAPE_DEFAULT = PIXEL_SHAPES.HEX;

/** Gaussian Blur extras */
export const GAUSSIAN_MIX_DEFAULT = 100;
export const GAUSSIAN_EDGE_PRESERVE_DEFAULT = 0;

export const BLUR_TARGETS = {
  FULL: "full",
  SUBJECT: "subject",
  BACKGROUND: "background",
};

export const BLUR_TARGET_OPTIONS = [
  { id: BLUR_TARGETS.FULL, label: "Full" },
  { id: BLUR_TARGETS.SUBJECT, label: "Subject" },
  { id: BLUR_TARGETS.BACKGROUND, label: "Background" },
];

export const BLUR_TARGET_DEFAULT = BLUR_TARGETS.FULL;

/** @deprecated kept for older session payloads — blur always composites over cutout. */
export const BLUR_MODES = {
  BACKGROUND: "background",
  OBJECT: "object",
  FULL: "full",
};

/**
 * Catalog tool_key → prep UI defaults.
 * Shared bake engine; variants differ by title, default type, and allowed type chips.
 * `allowedTypes: null` = full gallery (generic Blur tool).
 */
export const BLUR_TOOL_VARIANTS = {
  blur: {
    toolKey: "blur",
    title: "Blur",
    aka: "Photo Blur",
    subtitle:
      "All blur styles in one place — whole image, or subject only with Remove Background.",
    initialType: BLUR_TYPES.GAUSSIAN,
    allowedTypes: null,
    showPixelControls: false,
    showGaussianControls: false,
    lockBlurTarget: null,
    applyLabel: "Apply Blur",
    adjustLabel: "Adjust blur",
    shareTitle: "Blur tool",
    shareText: "I blurred my photo with LazyStudio's Blur tool!",
    downloadName: "blur.png",
    emptyPreview: "Upload an image to preview blur",
    thankYouLead: "Saved on your device with your blur settings applied.",
  },
  gaussian_blur: {
    toolKey: "gaussian_blur",
    title: "Gaussian Blur",
    aka: "Soft Blur",
    subtitle:
      "Soft Gaussian blur with mix, edge preserve, and full / subject / background targeting.",
    initialType: BLUR_TYPES.GAUSSIAN,
    allowedTypes: [BLUR_TYPES.GAUSSIAN],
    showPixelControls: false,
    showGaussianControls: true,
    lockBlurTarget: null,
    applyLabel: "Apply Gaussian Blur",
    adjustLabel: "Adjust blur",
    shareTitle: "Gaussian Blur tool",
    shareText: "I softened my photo with LazyStudio's Gaussian Blur tool!",
    downloadName: "gaussian-blur.png",
    emptyPreview: "Upload an image to preview Gaussian blur",
    thankYouLead: "Saved on your device with Gaussian blur applied.",
  },
  pixel_blur: {
    toolKey: "pixel_blur",
    title: "Pixel Blur",
    aka: "Pixelate",
    subtitle:
      "Hex, square, circle, or diamond mosaic with cell size, gap, softness, and mix.",
    initialType: BLUR_TYPES.PIXELATE,
    allowedTypes: [BLUR_TYPES.PIXELATE],
    showPixelControls: true,
    showGaussianControls: false,
    lockBlurTarget: null,
    applyLabel: "Apply Pixel Blur",
    adjustLabel: "Adjust pixel blur",
    shareTitle: "Pixel Blur tool",
    shareText: "I pixelated my photo with LazyStudio's Pixel Blur tool!",
    downloadName: "pixel-blur.png",
    emptyPreview: "Upload an image to preview pixel blur",
    thankYouLead: "Saved on your device with pixel blur applied.",
  },
  motion_blur: {
    toolKey: "motion_blur",
    title: "Motion Blur",
    aka: "Directional Blur",
    subtitle:
      "Directional motion blur for speed, action, and dynamic product shots.",
    initialType: BLUR_TYPES.MOTION,
    allowedTypes: [BLUR_TYPES.MOTION],
    showPixelControls: false,
    showGaussianControls: false,
    lockBlurTarget: null,
    applyLabel: "Apply Motion Blur",
    adjustLabel: "Adjust motion blur",
    shareTitle: "Motion Blur tool",
    shareText: "I added motion blur with LazyStudio's Motion Blur tool!",
    downloadName: "motion-blur.png",
    emptyPreview: "Upload an image to preview motion blur",
    thankYouLead: "Saved on your device with motion blur applied.",
  },
  bokeh_blur: {
    toolKey: "bokeh_blur",
    title: "Bokeh Blur",
    aka: "Depth Blur",
    subtitle:
      "Creamy bokeh-style blur for portraits, products, and dreamy depth of field.",
    initialType: BLUR_TYPES.BOKEH,
    allowedTypes: [BLUR_TYPES.BOKEH],
    showPixelControls: false,
    showGaussianControls: false,
    lockBlurTarget: null,
    applyLabel: "Apply Bokeh Blur",
    adjustLabel: "Adjust bokeh",
    shareTitle: "Bokeh Blur tool",
    shareText: "I added creamy bokeh with LazyStudio's Bokeh Blur tool!",
    downloadName: "bokeh-blur.png",
    emptyPreview: "Upload an image to preview bokeh blur",
    thankYouLead: "Saved on your device with bokeh blur applied.",
  },
  background_blur: {
    toolKey: "background_blur",
    title: "Background Blur",
    aka: "Portrait Blur",
    subtitle:
      "Blur only the background — subject stays sharp. Pick any blur style.",
    initialType: BLUR_TYPES.GAUSSIAN,
    allowedTypes: null,
    showPixelControls: false,
    showGaussianControls: false,
    lockBlurTarget: BLUR_TARGETS.BACKGROUND,
    applyLabel: "Apply Background Blur",
    adjustLabel: "Adjust background blur",
    shareTitle: "Background Blur tool",
    shareText: "I blurred the background with LazyStudio's Background Blur tool!",
    downloadName: "background-blur.png",
    emptyPreview: "Upload an image to preview background blur",
    thankYouLead: "Saved on your device with background blur applied.",
  },
};

export function resolveBlurToolVariant(toolKey) {
  return BLUR_TOOL_VARIANTS[toolKey] || BLUR_TOOL_VARIANTS.blur;
}

export function clampBlurAmount(value) {
  const n = Math.round(Number(value));
  if (!Number.isFinite(n)) return BLUR_AMOUNT_DEFAULT;
  return Math.max(BLUR_AMOUNT_MIN, Math.min(BLUR_AMOUNT_MAX, n));
}

export function clampPixelParam(value, fallback = 0) {
  const n = Math.round(Number(value));
  if (!Number.isFinite(n)) return fallback;
  return Math.max(0, Math.min(100, n));
}

export function resolvePixelShape(shape) {
  const match = PIXEL_SHAPE_OPTIONS.find((o) => o.id === shape);
  return match?.id || PIXEL_SHAPE_DEFAULT;
}

export function resolveBlurType(type, allowedTypes = null) {
  const match = BLUR_TYPE_OPTIONS.find((o) => o.id === type);
  const resolved = match?.id || BLUR_TYPES.GAUSSIAN;
  if (!allowedTypes || allowedTypes.length === 0) return resolved;
  if (allowedTypes.includes(resolved)) return resolved;
  return allowedTypes[0];
}

export function blurTypeLabel(type) {
  const match = BLUR_TYPE_OPTIONS.find((o) => o.id === type);
  return match?.label || "Gaussian";
}

export function blurTypeOptionsForVariant(variant) {
  const allowed = variant?.allowedTypes;
  if (!allowed || allowed.length === 0) return BLUR_TYPE_OPTIONS;
  return BLUR_TYPE_OPTIONS.filter((o) => allowed.includes(o.id));
}

/** Photoroom-style: cutout is optional (object blur when present). */
export function blurModeNeedsCutout() {
  return false;
}

export function resolveBlurTarget(target) {
  const match = BLUR_TARGET_OPTIONS.find((o) => o.id === target);
  return match?.id || BLUR_TARGET_DEFAULT;
}

export function blurTargetNeedsCutout(target) {
  const t = resolveBlurTarget(target);
  return t === BLUR_TARGETS.SUBJECT || t === BLUR_TARGETS.BACKGROUND;
}

export function createDefaultBlurSettings(toolKey = "blur") {
  const variant = resolveBlurToolVariant(toolKey);
  const lockedTarget = variant.lockBlurTarget
    ? resolveBlurTarget(variant.lockBlurTarget)
    : null;
  return {
    type: variant.initialType,
    amount: variant.showPixelControls
      ? PIXEL_CELL_SIZE_DEFAULT
      : BLUR_AMOUNT_DEFAULT,
    mode: BLUR_MODES.BACKGROUND,
    pixelShape: PIXEL_SHAPE_DEFAULT,
    pixelSoftness: PIXEL_SOFTNESS_DEFAULT,
    pixelGap: PIXEL_GAP_DEFAULT,
    pixelMix: PIXEL_MIX_DEFAULT,
    blurTarget: lockedTarget || BLUR_TARGET_DEFAULT,
    gaussianMix: GAUSSIAN_MIX_DEFAULT,
    edgePreserve: GAUSSIAN_EDGE_PRESERVE_DEFAULT,
  };
}

function randomInt(min, max) {
  const lo = Math.ceil(min);
  const hi = Math.floor(max);
  return Math.floor(Math.random() * (hi - lo + 1)) + lo;
}

function pickRandom(list) {
  if (!list?.length) return null;
  return list[Math.floor(Math.random() * list.length)];
}

/** Reset prep settings to the tool’s defaults. */
export function createResetBlurSettings(toolKey = "blur") {
  return createDefaultBlurSettings(toolKey);
}

/**
 * Randomize settings for the active blur tool (visible, useful ranges).
 */
export function createShuffledBlurSettings(toolKey = "blur") {
  const variant = resolveBlurToolVariant(toolKey);
  const defaults = createDefaultBlurSettings(toolKey);
  const typeOptions = blurTypeOptionsForVariant(variant);
  const next = {
    ...defaults,
    amount: randomInt(18, 72),
  };

  if (typeOptions.length > 1) {
    next.type = pickRandom(typeOptions)?.id || defaults.type;
  }

  if (variant.showPixelControls) {
    next.pixelShape = pickRandom(PIXEL_SHAPE_OPTIONS)?.id || PIXEL_SHAPE_DEFAULT;
    next.pixelGap = randomInt(0, 50);
    next.pixelSoftness = randomInt(0, 45);
    next.pixelMix = randomInt(60, 100);
    next.amount = randomInt(20, 68);
  }

  if (variant.showGaussianControls) {
    next.blurTarget = pickRandom(BLUR_TARGET_OPTIONS)?.id || BLUR_TARGET_DEFAULT;
    next.gaussianMix = randomInt(55, 100);
    next.edgePreserve = randomInt(0, 55);
    next.amount = randomInt(18, 65);
  }

  if (variant.lockBlurTarget) {
    next.blurTarget = resolveBlurTarget(variant.lockBlurTarget);
  }

  return next;
}

/**
 * Normalize pixel bake options from settings / type.
 * Generic Blur "Square Pixel" type maps to square shape.
 */
export function resolvePixelBakeOptions(settings = {}) {
  const type = settings.type || BLUR_TYPES.PIXELATE;
  let shape = resolvePixelShape(settings.pixelShape);
  if (type === BLUR_TYPES.SQUARE_BOKEH) shape = PIXEL_SHAPES.SQUARE;
  return {
    shape,
    amount: clampBlurAmount(settings.amount),
    gap: clampPixelParam(settings.pixelGap, PIXEL_GAP_DEFAULT),
    softness: clampPixelParam(settings.pixelSoftness, PIXEL_SOFTNESS_DEFAULT),
    mix: clampPixelParam(settings.pixelMix, PIXEL_MIX_DEFAULT),
  };
}

/**
 * CSS preview filter / style hints for live stage (approximate).
 */
export function getBlurPreviewStyle(type, displayPx, amount01 = 0) {
  const px = Math.max(0, Number(displayPx) || 0);
  const t = resolveBlurType(type);
  const strength = Math.max(0, Math.min(1, Number(amount01) || 0));

  if (px <= 0 && strength <= 0) return { filter: "none" };

  switch (t) {
    case BLUR_TYPES.PIXELATE: {
      const scale = Math.max(0.08, 0.55 - strength * 0.47);
      return { filter: "none", pixelateScale: scale, pixelateVariant: "classic" };
    }
    case BLUR_TYPES.SQUARE_BOKEH: {
      const scale = Math.max(0.04, 0.35 - strength * 0.31);
      return { filter: "none", pixelateScale: scale, pixelateVariant: "square" };
    }
    case BLUR_TYPES.MOTION:
      return {
        filter: "none",
        motionTrail: true,
        motionDistance: Math.max(4, px * 1.4),
      };
    case BLUR_TYPES.BOKEH:
      return {
        filter: `blur(${px * 1.2}px) saturate(1.15) brightness(1.04)`,
      };
    case BLUR_TYPES.BOX:
      return { filter: `blur(${Math.max(1, px * 0.85)}px)` };
    case BLUR_TYPES.DISC:
      return {
        filter: `blur(${px * 1.05}px) contrast(1.05)`,
      };
    case BLUR_TYPES.GAUSSIAN:
    default:
      return { filter: `blur(${px}px)` };
  }
}
